import { PageLoading } from '@ant-design/pro-components';
import { Result, message } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { Navigate, Outlet, useLocation, useParams } from 'react-router-dom';
import { getOrgRedirectPlan } from '@/apps/auth/utils/login';
import { authClient } from '@/common/utils/auth-client';

const ORGANIZATION_QUERY_STATUS = {
  LOADING: 'loading',
  SUCCESS: 'success',
  FAILURE: 'failure',
};

const fetchOrganizations = async () => {
  const { data, error } = await authClient.organization.list();
  if (error) {
    throw new Error(error.message || '获取组织列表失败');
  }
  return Array.isArray(data) ? data : [];
};

const parseMetadata = (metadata) => {
  if (!metadata) {
    return {};
  }
  if (typeof metadata === 'object') {
    return metadata;
  }
  try {
    return JSON.parse(metadata);
  } catch {
    return {};
  }
};

const getOrganizationStatus = (organization) => {
  const metadata = parseMetadata(organization?.metadata);
  return metadata.status || 'active';
};

const OrganizationGuard = () => {
  const [organizationState, setOrganizationState] = useState({
    organizations: [],
    error: undefined,
    status: ORGANIZATION_QUERY_STATUS.LOADING,
  });
  const location = useLocation();
  const { organizationId } = useParams();

  useEffect(() => {
    let active = true;
    setOrganizationState((prevState) => ({
      ...prevState,
      error: undefined,
      status: ORGANIZATION_QUERY_STATUS.LOADING,
    }));
    fetchOrganizations()
      .then((organizations) => {
        if (!active) {
          return;
        }
        setOrganizationState({
          organizations,
          error: undefined,
          status: ORGANIZATION_QUERY_STATUS.SUCCESS,
        });
      })
      .catch((error) => {
        if (!active) {
          return;
        }
        message.error(error.message);
        setOrganizationState({
          organizations: [],
          error,
          status: ORGANIZATION_QUERY_STATUS.FAILURE,
        });
      });
    return () => {
      active = false;
    };
  }, []);

  const currentOrganization = useMemo(() => {
    if (organizationState.status !== ORGANIZATION_QUERY_STATUS.SUCCESS) {
      return undefined;
    }
    return organizationState.organizations.find((org) => org.id === organizationId);
  }, [organizationId, organizationState]);

  const isCurrentOrganizationFrozen =
    currentOrganization && getOrganizationStatus(currentOrganization) === 'frozen';

  const redirectPlan = useMemo(() => {
    if (organizationState.status !== ORGANIZATION_QUERY_STATUS.SUCCESS) {
      return null;
    }
    if (isCurrentOrganizationFrozen) {
      return null;
    }
    return getOrgRedirectPlan({
      pathname: location.pathname,
      organizationId,
      organizations: organizationState.organizations,
      from: location.state?.from,
    });
  }, [
    isCurrentOrganizationFrozen,
    location.pathname,
    location.state,
    organizationId,
    organizationState,
  ]);

  useEffect(() => {
    if (!redirectPlan?.message) {
      return;
    }
    message[redirectPlan.message.type](redirectPlan.message.text);
  }, [redirectPlan]);

  if (organizationState.status === ORGANIZATION_QUERY_STATUS.LOADING) {
    return <PageLoading />;
  }

  if (organizationState.status === ORGANIZATION_QUERY_STATUS.FAILURE) {
    return (
      <Result
        status="error"
        title="组织信息加载失败"
        subTitle={organizationState.error?.message || '请稍后重试'}
      />
    );
  }

  if (isCurrentOrganizationFrozen) {
    return <Result status="403" title="组织已冻结" subTitle="该组织已冻结，暂不可使用" />;
  }

  if (redirectPlan) {
    return <Navigate to={redirectPlan.to} replace />;
  }

  return <Outlet />;
};

export default OrganizationGuard;
